// ============================================================================
// OpenHankAsAdminButton — F-014 surface (platform_admin tenant impersonation)
// ============================================================================
// Renders a button on a tenant's deployment row that mints a per-deployment
// JWT and opens the tenant's Hank URL with the token attached as a query
// parameter (Hank's central-mode auth middleware accepts `?token=` for the
// /api/events EventSource path; for the workspace shell the operator is
// expected to paste-or-handshake the token client-side).
//
// Why: F-014 platform_admin tenant-scope. Hank's tenant-resolver requires
// `token.tenant_id === HANK_TENANT_ID`. The platform_admin's Adam session
// JWT has audience=adam (no tenant binding), so navigating directly into a
// tenant Hank with that token would 403. CP mints a fresh hank-audience
// token scoped to the requested tenantId at click-time.
// ============================================================================

import { useState } from "react";
import { Button } from "@agentme/design-system";
import { useDeploymentToken } from "../hooks/useDeploymentToken";

export interface OpenHankAsAdminButtonProps {
  tenantId: string;
  hankUrl: string;
  /** Disable the button (e.g. when deployment is in deprovisioned/failed state). */
  disabled?: boolean;
}

export function OpenHankAsAdminButton(props: OpenHankAsAdminButtonProps): JSX.Element {
  const { tenantId, hankUrl, disabled } = props;
  const { mint, loading, error } = useDeploymentToken(tenantId);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleClick(): Promise<void> {
    setLocalError(null);
    // BugBot MEDIUM (PR #627): validate Hank URL before minting so an
    // empty/missing URL doesn't waste the 1-token-per-60s rate limit.
    const base = hankUrl?.trim();
    if (!base) {
      setLocalError("Deployment has no Hank URL configured");
      return;
    }
    try {
      const { token } = await mint();
      // Hank's central auth accepts ?token= for SSE; for protected routes
      // Adam can use this URL as the bootstrap entry, then the operator's
      // browser persists the token client-side. The Hank UI's auth boot
      // path picks up the query token and stores it for subsequent calls.
      const url = new URL(base);
      url.searchParams.set("token", token);
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === "string" ? err : "Mint failed";
      setLocalError(message);
    }
  }

  const surfaceError = localError ?? (error ? error.message : null);

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="primary" onClick={handleClick} disabled={disabled || loading}>
        {loading ? "Minting…" : "Open Hank as platform_admin"}
      </Button>
      {surfaceError && (
        <span className="text-xs" style={{ color: "var(--c-danger, #ef4444)" }}>
          {surfaceError}
        </span>
      )}
    </div>
  );
}
